import React, { Fragment, ReactNode, SetStateAction, useEffect, useRef, useState } from "react";
import { Location, useLocation } from "react-router-dom";
import { useGetPageTitle } from "helpers/useGetPageTitle";
import api, { useTable, TableInfo } from "api";
import { Spinner } from "components/Spinner/Spinner";
import { PageHeader } from "components/helpers";
import { useMenuState, CrudItems, CrudOptions } from "./CrudMenu";
import { useSaveData } from "helpers/useSaveData";
import { useGetPermissions } from "helpers/useGetPermissions";
import { FlexTable } from "components/newFlexTable";
import { PrimitiveFormatters, TableProps } from "components/newFlexTable/FlexTable";
import { Filter, FilterBuilder, Filters } from "components/helpers/filterBuilder/FilterBuilder";
import CrudMenu from "./CrudMenu";
import RowOptionsMenu from "./RowOptionMenu";
import Pagination from "components/pagination/Pagination";
import { DisableMenuItemData } from "pages/PlatformUsers/PlatformUsers";
import { useDispatch, useSelector } from "react-redux";
import {
  CustomRowCellColumns,
  CustomRowCellData,
  resetProgrammeId,
  resetRedirectUrl,
  resetRedirectUrlDestinationModule,
  resetRelatedFormReduxData,
  selectCloseRightMenu,
  selectCustomRowData,
  selectOriginFormData,
  selectParentId,
  selectPreviousProgrammeId,
  selectRelatedFormsIdsToDelete,
  setCustomRowData,
  setParentId,
  setPreviousProgrammeId,
  setProgrammeId,
  setRedirectUrl,
  setRelatedFormActionType,
  setRelatedFormData,
  setRelatedFormModuleName,
  setReloadProjectManagementProgrammeSelectorToggle,
  setResetCloseRightMenu,
} from "reducers/listPage";
import { useLoadScopes } from "helpers/auth";
import NoListIcon from "assets/Icon_Table_Empty.png";
import CardListPageToggle from "components/CardListPageToggle/CardListPageToggle";
import CardPage, {
  CardDataInformation,
  CardLayout3DataInformation,
  CardLayout4DataInformation,
  CardLayoutCustomFieldsDataInformation,
  CardLayoutGatewayDataInformation,
  CardTypeOptions,
} from "pages/Companies/CardPage";
import {
  ACTION_KEY,
  ACTION_SELECTED_ID_KEY,
  APP_ROUTES,
  FILTER_KEY,
  LIST_PARAM_NAME_KEY,
  LIST_PARAM_VALUE_KEY,
  PARENT_ID_KEY,
  endpoints,
} from "../../config";
import { useHandleFilterFromStorage } from "../../helpers/useHandleFilterFromStorage";
import { CellOptions } from "components/newFlexTable/Cell";
import { useHandleEntityPageLinkFromStorage } from "helpers/useHandleEntityPageLinkFromStorage";
import { RoleListData } from "pages/Roles/Roles";
import { TableOptions } from "@avamae/table";
import { NestedFormData, deleteUncompletedItems, redirectOnDone } from "components/Entity/Entity";
import { Dispatch } from "redux";
import ParentCrudMenu, {
  ParentCrudItems,
  useParentCrudMenuState,
} from "components/Category/ParentCrudMenu/ParentCrudMenu";
import { CrudItems as NestedCrudItems } from "components/Category/NestedCrudMenu/NestedCrudMenu";
import RowOptionParentCrudMenu from "./ParentCrudMenu/RowOptionParentCrudMenu";
import { selectCloseParentMenu, setResetCloseParentMenu } from "reducers/parentCrudMenu";
import NestedCrudMenu, { useNestedCrudMenuState } from "./NestedCrudMenu/NestedCrudMenu";
import {
  clearNestedFormModuleData,
  selectReloadNestedFormParentFormToggle,
  setCreatedDataParentFieldData,
  setNestedParentFormData,
  setReloadNestedFormParentFormToggle,
} from "reducers/nestedCrudMenu";
import { resetParentProgrammeId, selectParentPorgrammeId } from "reducers/customFields";
import { MenuItem } from "@react-pdf-viewer/core";
import {
  selectTemporaryFilters,
  setActionID,
  setActionKey,
  setTemporaryFilters,
} from "reducers/redirect";

export const DEFAULT_B_ACTIVE_FILTER: Filter = {
  columnKey: "__bActive",
  operator: "EQ",
  value: "1",
};

export function CheckBox(props: {
  isSelected: boolean;
  onChange(): void;
  name: React.ReactText;
}): JSX.Element {
  const { isSelected, onChange, name } = props;
  return (
    <div>
      <div>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onChange}
          id={String(name)}
          name={String(name)}
        />
        <label className="CustomInput" htmlFor={name.toString()} />
      </div>
    </div>
  );
}

export enum CardListPage {
  "Card" = "Card",
  "List" = "List",
}

export type TCustomCell<T> = {
  type: "SingleSelect" | "MultiSelect" | "ProgressBar" | "ViewDocument";
  dataSource: keyof T;
  cutomCell?: React.ReactNode;
};

export type CardProps<Data> = {
  cardTitleColumn?: keyof Data | (keyof Data)[];
  cardTypeColumn?: keyof Data;
  cardType2Column?: keyof Data;
  overwriteCardNoun?: string;
  includeTypeColumn: boolean;
  includeType2Column?: boolean;

  cardLayout?: CardTypeOptions;
  cardCustomIcon?: (data?: Data) => ReactNode;
  cardCustomContent?: (data?: Data) => ReactNode;
  cardContentClassname?: string;
  cardActionType?: Partial<NestedListItemActionTypes>;
  cardFooterButtonText?: string;
  cardFooterButtonDisabled?: boolean;
  projectID?: boolean;
  clientID?: boolean;
  combineTitleAndType?: boolean;
  hideMenuOptions?: boolean;
  imageCardColumn?: string;
  overrideFooterButtonText?: string;
};

/**
 * Props for the Category component
 * @forceDefaultCardOrListView - If true, the default view (card or list) will be forced on page load regardless of user preferences.
 */
export type Props<Data> = Omit<Omit<TableProps<Data>, "table">, "rowOptionComponent"> & {
  location: Location | undefined;
  noun: string;
  idColumn: string;
  listEndpointUrl: string;
  cardEndpointData?: { url: string; permissionKey: string };
  crudComponents: CrudItems<Data>;
  parentCrudComponents?: ParentCrudItems<Data>;
  hiddenColumns: (keyof Data)[];
  rowOptionComponent?: (menu: any, selectedIds: any[]) => TableProps["rowOptionComponent"];
  largeRightPanel?: boolean | ((action?: string | null) => boolean);
  disableMenuItemData?: DisableMenuItemData;
  defaultCardOrListView?: CardListPage;
  cardViewOnly?: boolean;
  forceDefaultCardOrListView?: boolean;
  hideHeaderTitle?: boolean;
  cardProps?: CardProps<Data>;
  showAddBulk?: string;
  showIdColumn?: boolean;
  rowCellOptions?: {
    [x: string]: CellOptions | undefined;
  };
  hideCardPage?: boolean;
  headerComp?: (props: {
    table: TableInfo;
    onDone(): void;
    //hiddenFields?: string[];
    [k: string]: any;
  }) => JSX.Element;
  customRowCells?: {
    [k in keyof Data]?: TCustomCell<Data>;
  };
  wrapperClassName?: string;
  customInitPageContent?: ({
    menuState,
    crudItems,
  }: {
    menuState?: ReturnType<typeof useMenuState>;
    crudItems?: CrudItems<Data, CrudOptions>;
  }) => React.ReactNode;
  primitiveFormatters?: PrimitiveFormatters;
  customRowCellColumn?: CustomRowCellColumns;
  hideRowOptionsMenuComponent?: boolean;
  showFilterHeaderButton?: boolean;
  hideFilterTableButton?: boolean;
  listCSV?: { url: string; noun: string };
  disableAddButton?: boolean;
  filterCardData?: { [columnName: string]: any };

  nestedListItemProps?: NestedListItemProps;
  parentSelectedId?: number;
  noAddButton?: boolean;
  overridePageTitle?: string;
  nestedFormData?: NestedFormData;
  extraOnCloseFunction?: (onDone?: () => void) => void;
  programmefilter?: JSX.Element;
  customPageHeaderButtons?: { [permissionsKey: string]: JSX.Element };
  rolePermissions?: RolePermissions<Data>;
  maxCardPageTitleLength?: number;
  centerCardPageTitle?: boolean;
  reloadTableToggle?: boolean;
  hideCardPageIcon?: boolean;
  pageHeaderClass?: string;
  oppositePermissions?: RolePermissions<Data>;
  leftTableReducedPadding?: boolean;
  defaultBActiveFilterColumnKey?: string;
  rightPanelListPage?: boolean;
  noLeftTableCheckboxes?: boolean;
  numberOfLeftColumns?: number;
  disableListSearch?: boolean;
  createPermissionsKey?: string;
  addBulkPermissionsKey?: string;
  temporaryOverrideFilters?: Filters;
  customContainerHeight?: string;
  hideLeftControlPanel?: boolean;
  overridePermissionsPathName?: string;
};

export type NestedListItemActionTypes =
  | "list"
  | "listCsv"
  | "view"
  | "create"
  | "edit"
  | "reassign"
  | "deploy"
  | "recall"
  | "delete"
  | "download"
  | "upload"
  | "favourite"
  | "download"
  | "assetsDateUpdate"
  | "assetsSnaggingUpdate"
  | "assetsUpdateStatus"
  | "dates"
  | "snagg"
  | "gateways-overview"
  | "sites-view"
  | "update"
  | "documents-view"
  | "assets-view";

export type EndpointDataToSendTypes = "selectedParentId";
export type RolePermissions<Data> = {
  [Key in keyof Partial<Data>]: string[];
};

export type NestedListItemProps = {
  urls: Partial<{
    [actionType in NestedListItemActionTypes]: string;
  }>;
  searchUrls?: { [fieldName: string]: string };
  endpointDataToSend?: {
    get: Partial<{
      [actionType in NestedListItemActionTypes]: Partial<{
        [dataType in EndpointDataToSendTypes]: string;
      }>;
    }>; // This will take the "selectedParentId" for the parent panel and pass it to the nested list page endpoints we call. The string is the name we want to assign to the value in the params/payload we want to send to the endpoints.

    post: Partial<{
      [actionType in NestedListItemActionTypes]: Partial<{
        [dataType in EndpointDataToSendTypes]: string;
      }>;
    }>; // This will take the "selectedParentId" for the parent panel and pass it to the nested list page endpoints we call. The string is the name we want to assign to the value in the params/payload we want to send to the endpoints.
  };
};

export const permissionsRequested = [
  "create",
  "edit",
  "view",
  "delete",
  "communications-list",
  "resendverificationlink",
  "overview",
  "assets-create",
  "assets-dates-update",
  "assets-delete",
  "assets-edit",
  "assets-list",
  "assets-snagging-update",
  "assets-view",
  "contacts-create",
  "contacts-delete",
  "contacts-edit",
  "contacts-list",
  "contacts-view",
  "snaggingassets-create",
  "snaggingassets-dates-update",
  "snaggingassets-delete",
  "snaggingassets-edit",
  "snaggingassets-list",
  "snaggingassets-snagging-update",
  "snaggingassets-view",
  "clone",
  "download",
  "upload",
  "favourite",
  "documents-create",
  "documents-edit",
  "documents-list",
  "documents-view",
  "documents-delete",
  "documents-download",
  "documents-upload",
  "documents-favourite",
  "dates-update",
  "statuses-update",
  "assets-create",
  "assets-delete",
  "assets-edit",
  "assets-list",
  "assets-view",
  "assets-dates-update",
  "assets-statuses-update",
  "assets-snagging-update",
  "assets-upload",
  "surveys-list",
  "surveys-create",
  "surveys-edit",
  "surveys-view",
  "surveys-delete",
  "surveys-recall",
  "surveys-deploy",
  "surveys-reassign",
  "workflows-list",
  "workflows-create",
  "workflows-view",
  "workflows-delete",
  "gateways-overview",
  "gateways-get-gateways",
  "documents-list",
  "communications-list",
  "update",
  "update-productmanager",
  "update-apclerk",
  "update-apcoordinator",
  "recall",
  "deploy",
  "reassign",
  "sites-view",
  "kpis-get-kpis",
  "edit-access-details",
  "changelog-list",
  "changelogs-list",
  "sites-upload",
  "contacts-upload",
  "catalogue-items-upload",
  "users-upload",
  "gateways-header-milestone-update",
  "create-dms",
  "edit-dms",
  "deactivate",
  "card-list",
  "documents-card-list",
  "assets-card-list",
];

export const CARD_OR_LIST_KEY = "CARD_OR_LIST";

function Category<Data>(props: Props<Data>) {
  const {
    location,
    idColumn,
    listEndpointUrl,
    cardEndpointData,
    crudComponents,
    parentCrudComponents,
    hiddenColumns,
    rowOptionComponent,
    largeRightPanel,
    disableMenuItemData,
    defaultCardOrListView: cardOrListPage = CardListPage.List,
    cardViewOnly = false,
    forceDefaultCardOrListView = false,
    hideHeaderTitle,
    cardProps,
    showAddBulk,
    rowCellOptions,
    hideCardPage = false,
    headerComp,
    customRowCells,
    wrapperClassName,
    customInitPageContent,
    primitiveFormatters,
    customRowCellColumn,
    hideRowOptionsMenuComponent,
    showFilterHeaderButton = true,
    hideFilterTableButton,
    listCSV,
    showIdColumn,
    filterCardData,
    nestedListItemProps,
    parentSelectedId,
    noAddButton,
    overridePageTitle,
    oppositePermissions,
    nestedFormData,
    extraOnCloseFunction,
    programmefilter,
    customPageHeaderButtons,
    rolePermissions,
    maxCardPageTitleLength,
    centerCardPageTitle,
    reloadTableToggle,
    hideCardPageIcon,
    pageHeaderClass,
    leftTableReducedPadding,
    disableAddButton,
    defaultBActiveFilterColumnKey,
    rightPanelListPage,
    noLeftTableCheckboxes,
    numberOfLeftColumns,
    disableListSearch,
    createPermissionsKey,
    addBulkPermissionsKey,
    temporaryOverrideFilters,
    customContainerHeight,
    hideLeftControlPanel,
    overridePermissionsPathName,
    ...tableProps
  } = props;

  // Card data:

  // Use the prop as default if present, otherwise fall back to localStorage
  const getInitialCardOrList = (): CardListPage => {
    const localStorageCardOrList = window.localStorage.getItem(CARD_OR_LIST_KEY);
    if (
      localStorageCardOrList === CardListPage.Card ||
      localStorageCardOrList === CardListPage.List
    ) {
      return localStorageCardOrList as CardListPage;
    }
    return cardOrListPage;
  };
  const [cardOrListState, setCardOrListState] = useState<CardListPage>(getInitialCardOrList());

  // Use computed value that respects cardViewOnly
  const cardOrList = cardViewOnly ? CardListPage.Card : cardOrListState;

  const parentProgrammeId = useSelector(selectParentPorgrammeId);

  useEffect(() => {
    // IXSTD-6024: If forceDefaultCardOrListView is true, we want to set the view to the default (cardOrListPage) on page load only, ignoring any previously saved user preference in local storage.
    if (forceDefaultCardOrListView && cardOrListPage) {
      setCardOrListState(cardOrListPage);
      return;
    }

    if (!cardViewOnly) {
      // Otherwise, check local storage for user preference
      const localStorageCardOrList = window.localStorage.getItem(CARD_OR_LIST_KEY);
      if (
        (localStorageCardOrList && localStorageCardOrList === CardListPage.Card) ||
        (localStorageCardOrList && localStorageCardOrList === CardListPage.List)
      ) {
        setCardOrListState(localStorageCardOrList);
      }
    }
  }, [cardViewOnly, forceDefaultCardOrListView]);

  useEffect(() => {
    // Only save to localStorage if not in cardViewOnly mode and the value actually comes from user interaction
    if (!cardViewOnly && !forceDefaultCardOrListView && cardOrListState) {
      window.localStorage.setItem(CARD_OR_LIST_KEY, cardOrListState);
    }
  }, [cardOrListState, cardViewOnly, forceDefaultCardOrListView]);

  useEffect(() => {
    table.reload();
  }, [reloadTableToggle]);

  // Use this as backup as sometimes the normal "location" returns undefined
  const backupLocation = useLocation();

  const pageTitle = useGetPageTitle(location ?? backupLocation);

  const permissions = useGetPermissions(
    permissionsRequested,
    location ?? backupLocation,
    overridePermissionsPathName
  );

  let additionalListEndpointQueryStringParameters: string = "";

  if (parentSelectedId && nestedListItemProps?.endpointDataToSend?.get?.list?.selectedParentId) {
    if (additionalListEndpointQueryStringParameters.length === 0) {
      additionalListEndpointQueryStringParameters += `?${nestedListItemProps?.endpointDataToSend?.get?.list?.selectedParentId}=${parentSelectedId}`;
    } else {
      additionalListEndpointQueryStringParameters += `&${nestedListItemProps?.endpointDataToSend?.get?.list?.selectedParentId}=${parentSelectedId}`;
    }
  }

  let finalListEndpointUrl = listEndpointUrl;

  const hasCardEndpointPermissions =
    cardEndpointData &&
    cardEndpointData.permissionKey &&
    permissions.get(cardEndpointData.permissionKey)
      ? permissions.get(cardEndpointData.permissionKey)
      : false;

  const useCardEndpoint = typeof cardEndpointData?.url === "string";

  let cardViewEnabled = false;

  if (
    !hideCardPage &&
    cardEndpointData &&
    cardEndpointData.url &&
    useCardEndpoint &&
    hasCardEndpointPermissions
  ) {
    cardViewEnabled = true;
  } else if (!hideCardPage && !useCardEndpoint) {
    cardViewEnabled = true;
  }

  if (
    cardViewEnabled &&
    cardOrList === CardListPage.Card &&
    cardEndpointData &&
    cardEndpointData.url
  ) {
    finalListEndpointUrl = cardEndpointData.url;
  }

  useEffect(() => {
    if (!cardViewEnabled && cardOrList === CardListPage.Card && !cardViewOnly) {
      setCardOrListState(CardListPage.List);
    }
  }, [cardOrList, cardViewEnabled, cardViewOnly]);

  let tableOptions: TableOptions = {
    url:
      additionalListEndpointQueryStringParameters.length > 0
        ? `${finalListEndpointUrl}${additionalListEndpointQueryStringParameters}`
        : finalListEndpointUrl,
  };

  const table = useTable<Data, Error>(tableOptions);
  const [customCellValue, setCustomCellValue] = useState<any>(
    table.data?.details.listData.map((d) => ({
      id: d[idColumn as keyof Data],
      ...(customRowCells
        ? Object.keys(customRowCells)?.reduce(
            (acc, key) => ({
              ...acc,
              [key]: d[key as keyof Data],
            }),
            {}
          )
        : {}),
    }))
  );

  const customRowCellData = useSelector(selectCustomRowData);

  useEffect(() => {
    const tempCustomCellValue = table.data?.details.listData.map((d) => ({
      id: d[idColumn as keyof Data],
      ...(customRowCells
        ? Object.keys(customRowCells)?.reduce(
            (acc, key) => ({
              ...acc,
              [key]: d[key as keyof Data], //1000 not 'Version 00'
            }),
            {}
          )
        : {}),
    }));

    setCustomCellValue(tempCustomCellValue);
  }, [table.data]);

  useEffect(() => {
    if (customCellValue) {
      let newCustomRowCellData: CustomRowCellData | null = {
        ...customRowCellData,
      };
      if (customRowCellColumn) {
        newCustomRowCellData = {};
        newCustomRowCellData[customRowCellColumn as CustomRowCellColumns] = customCellValue;
      }
      dispatch(setCustomRowData(newCustomRowCellData));
    }
  }, [customCellValue]);

  const tableContainerRef = useRef<any>(null);

  const hasMultiSelect = crudComponents.some((action) => action.showInMultiSelect);

  const { loading, data, error } = table;

  const [selectedIds, setSelectedIds] = useState<any[]>([]);
  const [clickedIdIfMultiIds, setClickedIdIfMultiIds] = useState<any>(null);
  const [noPermissionsMenuItems, setNoPermissionsMenuItems] = useState<string[]>([]);
  const changeSearch = data?.actions.changeSearch;

  const [visibleOptions, setVisibleOptions] = useState<string | number | null>(null);
  const temporaryFilters = useSelector(selectTemporaryFilters);

  // Check local storage for filters: see DeleteRoleModal.tsx for example use
  useHandleFilterFromStorage(table as TableInfo, temporaryFilters);
  // useHandleEntityPageLinkFromStorage(table as TableInfo);

  useEffect(() => {
    if (
      temporaryFilters &&
      temporaryOverrideFilters &&
      table?.data?.details?.filters !== temporaryOverrideFilters
    ) {
      const parsedTemporaryOverrideFilters = temporaryOverrideFilters.map(
        (item) => item.filters[0]
      );

      table?.data?.actions.setFilters(parsedTemporaryOverrideFilters);
    }
  }, [temporaryOverrideFilters, table, temporaryFilters]);
  // }, [temporaryOverrideFilters, table?.data?.details?.filters]);

  const selectedData = useSaveData<Data>(selectedIds, idColumn, table as TableInfo);

  const [menu, menuActions] = useMenuState<Data>({
    menuItems: crudComponents,
    selectedData,
    permissions,
    table: table as TableInfo,
    idColumn,
    disableMenuItemData,
    rolePermissions,
    oppositePermissions,
  });

  const [parentCrudMenu, parentCrudMenuActions, cardActions] = useParentCrudMenuState<Data>({
    menuItems: parentCrudComponents ?? null,
    selectedData,
    permissions,
    table: table as TableInfo,
    idColumn,
    disableMenuItemData,
  });

  const [
    nestedCrudMenu,
    nestedCrudMenuActions,
    // nestedCardActions,
  ] = useNestedCrudMenuState<Data>({
    menuItems: crudComponents ?? null,
    selectedData,
    permissions,
    table: table as TableInfo,
    idColumn,
    disableMenuItemData,
  });

  const scopes = useLoadScopes();

  const checkHasPermission = (permissionsKey: string) => {
    const locationPathName = location && location.pathname.slice(1); // remove "/" from start
    let hasPermission = false;
    if (locationPathName) {
      const relevantScopes = scopes.filter((item) => item.path === locationPathName);

      if (relevantScopes.length > 0) {
        const relevantScope = relevantScopes[0];

        if (relevantScope.childElements) {
          relevantScope.childElements.forEach((item) => {
            if (item.path === `${locationPathName}/${permissionsKey}`) {
              hasPermission = true;
            }
          });
        }
      }
    }
    return hasPermission;
  };

  useEffect(() => {
    let tempNoPermissionsMenuItems: string[] = [];
    parentCrudComponents
      ? parentCrudComponents.forEach((item) => {
          if (item.permissionsKey) {
            const hasPermission = checkHasPermission(item.permissionsKey);
            if (!hasPermission) {
              tempNoPermissionsMenuItems = [...tempNoPermissionsMenuItems, item.permissionsKey];
            }
          }
          setNoPermissionsMenuItems(tempNoPermissionsMenuItems);
        })
      : crudComponents.forEach((item) => {
          if (item.permissionsKey) {
            const hasPermission = checkHasPermission(item.permissionsKey as string);

            if (!hasPermission) {
              tempNoPermissionsMenuItems = [
                ...tempNoPermissionsMenuItems,
                item.permissionsKey as string,
              ];
            }
          }
          setNoPermissionsMenuItems(tempNoPermissionsMenuItems);
        });
  }, [crudComponents, parentCrudComponents, scopes]);

  const tableOptionComponent = (data: any) =>
    parentCrudComponents ? (
      <RowOptionParentCrudMenu
        location={location}
        data={data}
        menuState={[parentCrudMenu, parentCrudMenuActions, cardActions]}
        selectedIds={selectedIds}
        crudItems={parentCrudComponents}
        idColumn={data[idColumn]}
        visible={visibleOptions}
        setVisible={setVisibleOptions}
        disableMenuItemData={disableMenuItemData}
        noPermissionsMenuItems={noPermissionsMenuItems}
        additionalIdColumns={customCellValue?.filter((cv: any) => data[idColumn] === cv.id)}
      />
    ) : (
      <RowOptionsMenu
        location={location}
        data={data}
        menuState={[menu, menuActions]}
        selectedIds={selectedIds}
        crudItems={crudComponents}
        idColumn={data[idColumn]}
        visible={visibleOptions}
        setVisible={setVisibleOptions}
        disableMenuItemData={disableMenuItemData}
        noPermissionsMenuItems={noPermissionsMenuItems}
        additionalIdColumns={customCellValue?.filter((cv: any) => data[idColumn] === cv.id)}
      />
    );

  const previousProgrammeId = useSelector(selectPreviousProgrammeId);

  const clearTemporaryFilters = () => {
    if (temporaryFilters) {
      window.localStorage.removeItem(ACTION_SELECTED_ID_KEY);

      table.data?.actions.setFilters([]);

      dispatch(setActionKey(undefined));

      dispatch(resetRedirectUrl());
      dispatch(resetRedirectUrlDestinationModule());
      dispatch(setActionID(null));

      dispatch(setParentId(null));

      dispatch(setTemporaryFilters(undefined));

      dispatch(resetProgrammeId());

      window.localStorage.removeItem(PARENT_ID_KEY);
      window.localStorage.removeItem(ACTION_KEY);
      window.localStorage.removeItem(ACTION_SELECTED_ID_KEY);

      if (
        window &&
        window.location &&
        window.location.pathname &&
        window.location.pathname === APP_ROUTES.projectmanagement
      ) {
        dispatch(setReloadProjectManagementProgrammeSelectorToggle());
      }
    }
  };

  // DEFAULT B ACTIVE FILTER:
  useEffect(() => {
    if (
      !temporaryFilters &&
      defaultBActiveFilterColumnKey &&
      table?.data?.details?.filters &&
      Array.isArray(table?.data?.details?.filters) &&
      table?.data?.details?.filters.length === 0
    ) {
      let tempDefaultBActiveFilter = DEFAULT_B_ACTIVE_FILTER;

      tempDefaultBActiveFilter.columnKey = defaultBActiveFilterColumnKey;

      table.data?.actions.setFilters([tempDefaultBActiveFilter]);
    } else if (
      !temporaryFilters &&
      defaultBActiveFilterColumnKey &&
      table?.data?.details?.filters &&
      Array.isArray(table?.data?.details?.filters) &&
      table?.data?.details?.filters.length > 0
    ) {
      const match = table?.data?.details?.filters.filter(
        (item) => item.columnKey === defaultBActiveFilterColumnKey
      );

      if (!match || (match && match.length === 0)) {
        let tempDefaultBActiveFilter = DEFAULT_B_ACTIVE_FILTER;

        tempDefaultBActiveFilter.columnKey = defaultBActiveFilterColumnKey;

        const parsedExistingFilters: any = table?.data?.details?.filters.map((item) => {
          return {
            columnKey: item.columnKey,
            operator: item.filters[0].operator,
            value: item.filters[0].value,
          };
        });

        const newFilters = [...parsedExistingFilters, tempDefaultBActiveFilter];

        table.data?.actions.setFilters(newFilters);
      }
    }
  }, [table?.data?.details?.filters]);

  const onActionComplete = () => {
    nestedCrudMenuOnDone();

    if (
      window &&
      window.location &&
      window.location.pathname &&
      window.location.pathname != APP_ROUTES.projectmanagement
    ) {
      clearTemporaryFilters();
    }

    const openMenu = menu.currentMenu;

    if (openMenu) {
      menuActions.closeMenus();
    }

    setSelectedIds([]);

    if (openMenu !== "FILTERS") {
      table.reload();
    }

    dispatch(setCreatedDataParentFieldData(null));
  };

  const onParentActionComplete = () => {
    nestedCrudMenuOnDone();

    clearTemporaryFilters();

    const openMenu = parentCrudMenu.currentMenu;

    if (openMenu) {
      parentCrudMenuActions.closeMenus();
    }

    setSelectedIds([]);

    if (openMenu !== "FILTERS") {
      table.reload();
    }
  };

  const onNestedActionComplete = () => {
    dispatch(clearNestedFormModuleData());
    dispatch(setReloadNestedFormParentFormToggle(true));

    const openMenu = nestedCrudMenu.currentMenu;

    if (openMenu) {
      nestedCrudMenuActions.closeMenus();
    }
  };

  const closeRightMenu = useSelector(selectCloseRightMenu);
  const closeParentMenu = useSelector(selectCloseParentMenu);

  const dispatch = useDispatch();

  useEffect(() => {
    if (closeRightMenu && menuActions && menuActions.closeMenus) {
      if (menu.currentMenu) {
        menuActions.closeMenus();
      }
      setSelectedIds([]);

      table.reload();

      dispatch(setResetCloseRightMenu());
    }
  }, [closeRightMenu]);

  useEffect(() => {
    if (closeParentMenu && parentCrudMenuActions) {
      parentCrudMenuActions.closeMenus();
      setSelectedIds([]);
      table.reload();
      dispatch(setResetCloseParentMenu());

      // clearTemporaryFilters(); // NOTE: Don't think this is needed, leaving just in case
    }
  }, [closeParentMenu]);

  const headerElementClearFilters = () => {
    if (defaultBActiveFilterColumnKey) {
      let tempDefaultBActiveFilter = DEFAULT_B_ACTIVE_FILTER;

      tempDefaultBActiveFilter.columnKey = defaultBActiveFilterColumnKey;

      table.data?.actions.setFilters([tempDefaultBActiveFilter]);
    } else {
      table.data?.actions.setFilters([]);
    }
    parentCrudComponents ? onParentActionComplete() : onActionComplete();
  };

  let showClearFiltersButton = false;

  if (table.data && table.data.details.filters.length > 0) {
    showClearFiltersButton = true;

    if (defaultBActiveFilterColumnKey) {
      const defaultFilterMatch = table.data.details.filters.filter(
        (item) => item.columnKey === defaultBActiveFilterColumnKey
      );

      if (
        table.data.details.filters.length === 1 &&
        defaultFilterMatch &&
        defaultFilterMatch.length === 1 &&
        defaultFilterMatch[0].filters &&
        Array.isArray(defaultFilterMatch[0].filters) &&
        defaultFilterMatch[0].filters.length === 1 &&
        typeof defaultFilterMatch[0].filters[0] === "object" &&
        defaultFilterMatch[0].filters[0].columnKey === defaultBActiveFilterColumnKey &&
        defaultFilterMatch[0].filters[0].operator === "EQ" &&
        defaultFilterMatch[0].filters[0].value === "1"
      ) {
        showClearFiltersButton = false;
      }
    }
  }

  useEffect(() => {
    if (nestedCrudMenuActions && nestedFormData && nestedFormData.actionType) {
      if (
        nestedFormData.actionType === "view" &&
        nestedFormData.selectedId &&
        nestedCrudMenu.currentMenu !== "VIEW"
      ) {
        nestedCrudMenuActions.openMenu("VIEW", nestedFormData.selectedId);
      } else if (
        nestedFormData.actionType === "create" &&
        !nestedFormData.selectedId &&
        nestedCrudMenu.currentMenu !== "CREATE"
      ) {
        nestedCrudMenuActions.openMenu("CREATE", null);
      }
    } else if (!nestedFormData && nestedCrudMenu.currentMenu) {
      nestedCrudMenuActions.closeMenus();
    }
  }, [nestedFormData, nestedCrudMenuActions]);

  const nestedCrudMenuOnDone = () => {
    if (nestedFormData) {
      dispatch(clearNestedFormModuleData());
    }
    if (nestedCrudMenuActions && nestedCrudMenu.currentMenu) {
      nestedCrudMenuActions.closeMenus();
    }
  };

  const [menuActionsCode, setMenuActionsCode] = useState<CrudOptions>();

  const filteredCustomPageHeaderButtons = customPageHeaderButtons
    ? Object.entries(customPageHeaderButtons)
        .filter(([key, value]) => {
          return typeof key === "string" && permissions.has(key) && permissions.get(key);
        })
        .map((item) => item[1])
    : [];

  const hasCreatePermissions = permissions.has(createPermissionsKey ?? "create")
    ? permissions.get(createPermissionsKey ?? "create")
    : false;

  const hasUploadPermissions = permissions.has(addBulkPermissionsKey ?? "upload")
    ? permissions.get(addBulkPermissionsKey ?? "upload")
    : false;

  if (loading && !table.data) return <Spinner />;

  if (error) throw error;

  if (data) {
    const initialSearchString = data.details.searchString;

    return (
      <div
        data-testid="category-container"
        style={{
          display: "flex",
          height: customContainerHeight ?? "calc(100vh - 61px)",
        }}
        className={`${wrapperClassName} ${menu.currentMenu}`}
        ref={tableContainerRef}
      >
        <div
          className={`WidthContent HeightContent CategoryPage ${
            customInitPageContent ? "InitPageContent" : ""
          } ${nestedFormData ? "NestedCategoryPage" : ""}`}
        >
          <div className="HeaderContainer">
            {!hideLeftControlPanel && (
              <div className={`LeftControlPanel ${programmefilter ? "WithProgrammeFilter" : ""}`}>
                {!hideCardPage && !cardViewOnly ? (
                  <CardListPageToggle
                    page={cardOrList}
                    onOptionSelected={() => setSelectedIds([])}
                    setCardList={setCardOrListState}
                    cardViewEnabled={cardViewEnabled}
                  />
                ) : (
                  <div className="CardListToggleContainerPlaceholder"></div> // Placeholder for flex box
                )}
                {headerComp &&
                  headerComp({
                    onDone: parentCrudComponents ? onParentActionComplete : onActionComplete,
                    table: table as TableInfo,
                  })}
                {programmefilter && programmefilter}
              </div>
            )}
            <PageHeader
              onOpenFilters={() =>
                parentCrudComponents
                  ? parentCrudMenuActions.openMenu("FILTERS", null)
                  : menuActions.openMenu("FILTERS", null)
              }
              addButtonText={`Add new ${props.noun}`}
              onAddButtonClick={
                hasCreatePermissions && !noAddButton
                  ? () => menuActions.openMenu("CREATE", null)
                  : undefined
              }
              pageTitle={pageTitle}
              table={table as TableInfo}
              disableListSearch={disableListSearch}
              initialSearchString={initialSearchString ?? ""}
              changeSearch={changeSearch}
              hideHeaderTitle={hideHeaderTitle}
              disableAddButton={disableAddButton}
              addBulkButtonText={showAddBulk ? showAddBulk : undefined}
              onAddBulkButtonClick={
                hasUploadPermissions ? () => menuActions.openMenu("CREATE_BULK", null) : undefined
              }
              showFilterButton={showFilterHeaderButton}
              filterButtonOnClick={() =>
                parentCrudComponents
                  ? parentCrudMenuActions.openMenu("FILTERS", null)
                  : menuActions.openMenu("FILTERS", null)
              }
              listCSV={listCSV ? listCSV : undefined}
              fetchConfig={
                nestedListItemProps?.endpointDataToSend?.get?.listCsv?.selectedParentId
                  ? {
                      params: {
                        [(nestedListItemProps?.endpointDataToSend?.get?.listCsv
                          ?.selectedParentId as string)!]: parentSelectedId,
                      },
                    }
                  : undefined
              }
              overridePageTitle={overridePageTitle}
              programmeFilter={programmefilter}
              customButtons={filteredCustomPageHeaderButtons}
              styleClass={pageHeaderClass}
              defaultBActiveFilterColumnKey={defaultBActiveFilterColumnKey}
            />
          </div>
          {cardOrList === CardListPage.Card && !hideCardPage && (
            <div
              className=""
              style={{
                display: "flex",
              }}
            >
              <CardPage
                idColumn={idColumn}
                table={table as TableInfo}
                titleColumn={cardProps?.cardTitleColumn as string | string[]}
                typeColumn={cardProps?.cardTypeColumn as string}
                includeTypeColumn={cardProps?.includeTypeColumn}
                type2Column={cardProps?.cardType2Column as string}
                includeType2Column={cardProps?.includeType2Column}
                layout={cardProps?.cardLayout}
                actionType={cardProps?.cardActionType}
                customContent={cardProps?.cardCustomContent}
                customIcon={cardProps?.cardCustomIcon}
                contentClassname={cardProps?.cardContentClassname}
                filterCardData={filterCardData}
                projectID={cardProps?.projectID}
                clientID={cardProps?.clientID}
                combineTitleAndType={cardProps?.combineTitleAndType}
                hideMenuOptions={cardProps?.hideMenuOptions}
                imageCardColumn={cardProps?.imageCardColumn}
                maxTitleLength={maxCardPageTitleLength}
                centerCardPageTitle={centerCardPageTitle}
                hideIcon={hideCardPageIcon}
                crudItems={parentCrudComponents ? parentCrudComponents : crudComponents}
                permissions={permissions}
                disableMenuItemData={disableMenuItemData}
                rolePermissions={rolePermissions}
                oppositePermissions={oppositePermissions}
                LargeRightPanel={largeRightPanel}
                overrideFooterButtonText={cardProps?.overrideFooterButtonText}
              />
            </div>
          )}

          {(cardOrList === CardListPage.List || hideCardPage) &&
            ((table.data?.details?.listData && table.data?.details?.listData?.length > 0) ||
              !customInitPageContent) && (
              <div
                className="FlexTableContainer"
                style={{
                  display: "flex",
                }}
              >
                <FlexTable
                  idColumn={idColumn}
                  table={table as TableInfo}
                  hiddenColumns={hiddenColumns}
                  onFilterClick={() =>
                    parentCrudComponents
                      ? parentCrudMenuActions.openMenu("FILTERS", null)
                      : menuActions.openMenu("FILTERS", null)
                  }
                  checkbox={
                    !noLeftTableCheckboxes
                      ? {
                          selectedIds,
                          setSelectedIds,
                          idColumn,
                          clickedIdIfMultiIds,
                          setClickedIdIfMultiIds,
                        }
                      : undefined
                  }
                  rowOptionComponent={tableOptionComponent}
                  disableMultiSelect={!hasMultiSelect}
                  {...tableProps}
                  rowCellOptions={rowCellOptions}
                  customRowCells={customRowCells}
                  setCustomCellValue={setCustomCellValue}
                  customCellValue={customCellValue}
                  primitiveFormatters={primitiveFormatters}
                  hideRowOptionsMenuComponent={hideRowOptionsMenuComponent}
                  hideFilterTableButton={hideFilterTableButton}
                  showIdColumn={showIdColumn}
                  menuActionsCode={"VIEW"}
                  setMenuActionsCode={setMenuActionsCode}
                  parentSelectedId={parentSelectedId}
                  leftTableReducedPadding={leftTableReducedPadding}
                  splitFromLeft={numberOfLeftColumns}
                  refs={{
                    containerRef: tableContainerRef,
                  }}
                />
              </div>
            )}

          {table &&
            table.data &&
            table.data.details &&
            table.data.details.listData &&
            Array.isArray(table.data.details.listData) &&
            table.data.details.listData.length === 0 &&
            (customInitPageContent ? (
              customInitPageContent({
                menuState: [menu, menuActions],
                crudItems: crudComponents,
              })
            ) : (
              <div className="NoListData">
                <img className="NoListDataIcon" src={NoListIcon} alt="No results" />
                <div className="Text">No results</div>
              </div>
            ))}

          {(!customInitPageContent ||
            (table.data?.details?.listData && table.data?.details?.listData?.length > 0)) && (
            <Pagination table={table} />
          )}
        </div>

        {!parentCrudComponents && (
          <CrudMenu
            menu={menu}
            selectedData={selectedData.length > 0 ? selectedData : menu.selectedData}
            menuItems={menu.menuItems}
            crudItems={crudComponents}
            onRightMenuClose={() => {
              setSelectedIds([]);
            }}
            closePanel={() => {
              // On click outside right panel:
              if (
                window &&
                window.location &&
                window.location.pathname &&
                window.location.pathname != APP_ROUTES.projectmanagement
              ) {
                clearTemporaryFilters();
              }

              dispatch(setCreatedDataParentFieldData(null));

              dispatch(setNestedParentFormData(null));

              nestedCrudMenuOnDone();

              // Delete unused created items in nested form
              const onDone = () => {
                if (menuActionsCode) setMenuActionsCode(null);

                if (menu.currentMenu) {
                  menuActions.closeMenus();
                }
                table.reload();
              };
              if (extraOnCloseFunction) {
                // Pass this in below: onDone
                extraOnCloseFunction(onDone);
              } else {
                onDone();
              }
              if (parentProgrammeId) {
                dispatch(resetParentProgrammeId());
              }
            }}
            onDone={() => {
              onActionComplete();
              if (parentProgrammeId) {
                dispatch(resetParentProgrammeId());
              }
              if (menuActionsCode) setMenuActionsCode(null);
            }}
            filters={
              <FilterBuilder
                onDone={onActionComplete}
                table={table as TableInfo}
                hiddenFields={hiddenColumns}
                handleCancel={menuActions.closeMenus}
              />
            }
            filtersHeaderElement={
              <div className={`ClearFilters ${!showClearFiltersButton ? "Hide" : ""}`}>
                <div className="Btn" onClick={headerElementClearFilters}>
                  <div className="Text">Clear Filters</div>
                </div>
              </div>
            }
            largeRightPanel={largeRightPanel}
            idColumn={idColumn}
            additionalData={customCellValue?.filter((cv: any) => {
              if (selectedData.length > 0) {
                return !!selectedData?.find((sd) => sd[idColumn as keyof typeof sd] === cv.id);
              } else {
                return !!menu.selectedData?.some((sd: any) => sd[idColumn] === cv.id);
              }
            })}
            menuActionsCode={menuActionsCode}
            autoOpenPanel={(menu) => menuActions.openMenu(menu, null)}
            clickedIdIfMultiIds={clickedIdIfMultiIds}
          />
        )}

        {parentCrudComponents && (
          <ParentCrudMenu
            menu={parentCrudMenu}
            selectedData={selectedData.length > 0 ? selectedData : parentCrudMenu.selectedData}
            onRightMenuClose={() => {
              setSelectedIds([]);
            }}
            menuItems={parentCrudMenu.menuItems}
            parentCrudItems={parentCrudComponents}
            largeRightPanel={largeRightPanel}
            filters={
              <FilterBuilder
                onDone={onParentActionComplete}
                table={table as TableInfo}
                hiddenFields={hiddenColumns}
                handleCancel={parentCrudMenuActions.closeMenus}
              />
            }
            filtersHeaderElement={
              <div className={`ClearFilters ${!showClearFiltersButton ? "Hide" : ""}`}>
                <div className="Btn" onClick={headerElementClearFilters}>
                  <div className="Text">Clear Filters</div>
                </div>
              </div>
            }
            closePanel={() => {
              // On click outside right panel:

              nestedCrudMenuOnDone();

              clearTemporaryFilters();

              // Delete unused created items in nested form

              const onDone = () => {
                if (menuActionsCode) setMenuActionsCode(null);

                parentCrudMenuActions.closeMenus();
                table.reload();
              };
              if (extraOnCloseFunction) {
                // Pass this in below: onDone
                extraOnCloseFunction(onDone);
              } else {
                onDone();
              }
            }}
            onDone={() => {
              onParentActionComplete();
              if (menuActionsCode) setMenuActionsCode(null);
            }}
            idColumn={idColumn}
            menuActionsCode={menuActionsCode}
            autoOpenPanel={(menu) => parentCrudMenuActions.openMenu(menu, null)}
            clickedIdIfMultiIds={clickedIdIfMultiIds}
          />
        )}

        {crudComponents && nestedFormData && (
          <NestedCrudMenu
            menu={nestedCrudMenu}
            selectedData={selectedData.length > 0 ? selectedData : menu.selectedData}
            onRightMenuClose={() => {
              setSelectedIds([]);
            }}
            menuItems={nestedCrudMenu.menuItems}
            crudItems={crudComponents}
            filters={
              <FilterBuilder
                onDone={onNestedActionComplete}
                table={table as TableInfo}
                hiddenFields={hiddenColumns}
                handleCancel={() => {
                  if (parentCrudMenuActions) {
                    parentCrudMenuActions.closeMenus;
                  }
                }}
              />
            }
            filtersHeaderElement={
              <div className={`ClearFilters ${!showClearFiltersButton ? "Hide" : ""}`}>
                <div className="Btn" onClick={headerElementClearFilters}>
                  <div className="Text">Clear Filters</div>
                </div>
              </div>
            }
            closePanel={() => {
              // On click outside right panel:

              nestedCrudMenuOnDone();

              // Delete unused created items in nested form
              const onDone = () => {
                if (menu.currentMenu) {
                  menuActions.closeMenus();
                }
                table.reload();
              };
              if (extraOnCloseFunction) {
                // Pass this in below: onDone
                extraOnCloseFunction(onDone);
              } else {
                onDone();
              }
            }}
            onDone={onNestedActionComplete}
            idColumn={idColumn}
          />
        )}
      </div>
    );
  }

  return <Spinner />;
}

export default Category;
